import React, { useMemo, useRef, useState, useCallback, useEffect } from 'react';
import { Card, Button, LoadingSpinner, Pagination } from 'components/common';
import PropTypes from 'prop-types';
import { AgGridReact } from 'ag-grid-react';
import { FiList, FiDownload, FiFileText, FiPlusSquare, FiLayers } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import apiAxios from 'helpers/apiAxios';
import { ExportToExcel } from 'helpers/utils';
import { countryName } from './countryNames';

const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const fmtDate = (iso) => {
  if (!iso) return '—';
  const p = String(iso).split('-');
  if (p.length !== 3) return iso;
  return `${p[2].padStart(2,'0')}-${MONTHS_SHORT[parseInt(p[1],10)-1]}-${p[0]}`;
};

const fmtM = (n) => {
  const v = Number(n || 0);
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (v >= 1_000)     return `$${(v / 1_000).toFixed(0)}K`;
  return `$${v.toFixed(0)}`;
};

const JLink = ({ data, value, pinned }) => {
  if (pinned) return <span className="tw-font-bold tw-text-gray-700 tw-text-xs">Grand Total</span>;
  return data?.deal_id ? (
    <Link to={`/crm/projects/${data.deal_id}`} target="_blank" rel="noreferrer"
      className="tw-font-mono tw-font-bold tw-text-blue-700 hover:tw-underline">
      {value || data?.q_number || '—'}
    </Link>
  ) : (value || '—');
};
JLink.propTypes = {
  data:   PropTypes.shape({ deal_id: PropTypes.number, q_number: PropTypes.string }),
  value:  PropTypes.string,
  pinned: PropTypes.string
};
JLink.defaultProps = { data: null, value: null, pinned: null };

const CONTRACT_COLS = [
  { headerName: 'J Number', field: 'j_number', width: 115, pinned: 'left', sortable: true, filter: 'agTextColumnFilter',
    cellRenderer: p => <JLink data={p.data} value={p.value} pinned={p.node?.rowPinned} /> },
  { headerName: 'Contract #',  field: 'contract_number', width: 130, sortable: true, filter: 'agTextColumnFilter' },
  { headerName: 'Project',     field: 'project_name',    flex: 2, minWidth: 150, sortable: true, filter: 'agTextColumnFilter' },
  { headerName: 'Customer',    field: 'customer_name',   flex: 1, minWidth: 120, sortable: true, filter: 'agTextColumnFilter' },
  { headerName: 'Contract Date', field: 'contract_date', width: 120, sortable: true, filter: 'agDateColumnFilter',
    valueFormatter: p => fmtDate(p.value), cellStyle: { color: '#6B7280', fontSize: '12px' } },
  { headerName: 'Contract Value', field: 'contract_value', width: 130, sortable: true, type: 'numericColumn', filter: 'agNumberColumnFilter',
    valueFormatter: p => fmtM(p.value), cellStyle: { textAlign: 'right', fontWeight: 600, color: '#1D4ED8' } },
  { headerName: 'Addendum Value', field: 'addendum_value', width: 130, sortable: true, type: 'numericColumn', filter: 'agNumberColumnFilter',
    valueFormatter: p => p.value > 0 ? fmtM(p.value) : '—', cellStyle: { textAlign: 'right', color: '#D97706' } },
  { headerName: 'Total Value', field: 'grand_total_value', width: 130, sortable: true, type: 'numericColumn', filter: 'agNumberColumnFilter',
    valueFormatter: p => fmtM(p.value), cellStyle: { textAlign: 'right', fontWeight: 700, color: '#059669' } },
  { headerName: 'Weight (MT)', field: 'contract_weight', width: 110, sortable: true, type: 'numericColumn', filter: 'agNumberColumnFilter',
    valueFormatter: p => Number(p.value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }), cellStyle: { textAlign: 'right' } },
  { headerName: 'Addendums', field: 'addendum_count', width: 95, sortable: true, type: 'numericColumn', filter: 'agNumberColumnFilter',
    cellStyle: { textAlign: 'center' }, valueFormatter: p => p.value > 0 ? p.value : '—' },
  { headerName: 'Released By', field: 'released_by', width: 120, sortable: true, filter: 'agTextColumnFilter',
    cellStyle: { fontSize: '12px', color: '#374151' } },
  { headerName: 'Released Date', field: 'released_date', width: 120, sortable: true, filter: 'agDateColumnFilter',
    valueFormatter: p => fmtDate(p.value), cellStyle: { fontSize: '12px', color: '#059669' } },
  { headerName: 'Sales Unit',     field: 'unit_name',      width: 110, sortable: true, filter: 'agTextColumnFilter', cellStyle: { fontSize: '12px' } },
  { headerName: 'Region',         field: 'region',         width: 105, sortable: true, filter: 'agTextColumnFilter', cellStyle: { fontSize: '12px', color: '#6B7280' } },
  { headerName: 'Type',           field: 'type_of_sales',  width: 95,  sortable: true, filter: 'agTextColumnFilter', cellStyle: { fontSize: '12px', color: '#6B7280' } },
  { headerName: 'Building',       field: 'building_name',  width: 130, sortable: true, filter: 'agTextColumnFilter', cellStyle: { fontSize: '12px', color: '#374151' } },
  { headerName: 'Sales Incharge', field: 'sales_incharge', width: 130, sortable: true, filter: 'agTextColumnFilter', cellStyle: { fontSize: '12px' } },
  { headerName: 'Province', field: 'pj_province', width: 120, sortable: true, filter: 'agTextColumnFilter', cellStyle: { fontSize: '12px', color: '#6B7280' } },
  { headerName: 'Country',  field: 'pj_country',  width: 120, sortable: true, filter: 'agTextColumnFilter', cellStyle: { fontSize: '12px' },
    valueFormatter: p => countryName(p.value) }
];

const ADDENDUM_COLS = [
  { headerName: 'J Number', field: 'j_number', width: 115, pinned: 'left', sortable: true, filter: 'agTextColumnFilter',
    cellRenderer: p => <JLink data={p.data} value={p.value} pinned={p.node?.rowPinned} /> },
  { headerName: 'Contract #',  field: 'contract_number', width: 130, sortable: true, filter: 'agTextColumnFilter',
    cellStyle: { fontSize: '12px', color: '#6B7280' } },
  { headerName: 'Addendum #',  field: 'addendum_number', width: 80,  sortable: true, filter: 'agNumberColumnFilter',
    cellStyle: { textAlign: 'center', fontWeight: 600, color: '#B45309' } },
  { headerName: 'Project',     field: 'project_name',    flex: 2, minWidth: 150, sortable: true, filter: 'agTextColumnFilter' },
  { headerName: 'Customer',    field: 'customer_name',   flex: 1, minWidth: 160, sortable: true, filter: 'agTextColumnFilter' },
  { headerName: 'Add. Date',   field: 'addendum_date',   width: 125, sortable: true, filter: 'agDateColumnFilter',
    valueFormatter: p => fmtDate(p.value), cellStyle: { color: '#B45309', fontSize: '12px' } },
  { headerName: 'Contract Date', field: 'contract_date', width: 120, sortable: true, filter: 'agDateColumnFilter',
    valueFormatter: p => fmtDate(p.value), cellStyle: { color: '#6B7280', fontSize: '12px' } },
  { headerName: 'Add. Value', field: 'addendum_value', width: 120, sortable: true, type: 'numericColumn', filter: 'agNumberColumnFilter',
    valueFormatter: p => p.value !== 0 ? fmtM(p.value) : '—',
    cellStyle: p => ({ textAlign: 'right', fontWeight: 600, color: p.value < 0 ? '#DC2626' : '#D97706' }) },
  { headerName: 'Add. Weight (MT)', field: 'addendum_weight', width: 135, sortable: true, type: 'numericColumn', filter: 'agNumberColumnFilter',
    valueFormatter: p => Number(p.value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }), cellStyle: { textAlign: 'right' } },
  { headerName: 'Parent Value', field: 'parent_contract_value', width: 120, sortable: true, type: 'numericColumn', filter: 'agNumberColumnFilter',
    valueFormatter: p => fmtM(p.value), cellStyle: { textAlign: 'right', color: '#1D4ED8', fontSize: '12px' } },
  { headerName: 'Signed By',    field: 'signed_by',    width: 100, sortable: true, filter: 'agTextColumnFilter', cellStyle: { fontSize: '12px', color: '#6B7280' } },
  { headerName: 'Released By',  field: 'released_by',  width: 120, sortable: true, filter: 'agTextColumnFilter', cellStyle: { fontSize: '12px', color: '#374151' } },
  { headerName: 'Released Date', field: 'released_date', width: 120, sortable: true, filter: 'agDateColumnFilter',
    valueFormatter: p => fmtDate(p.value), cellStyle: { fontSize: '12px', color: '#059669' } },
  { headerName: 'Building',       field: 'building_name',  width: 130, sortable: true, filter: 'agTextColumnFilter', cellStyle: { fontSize: '12px', color: '#374151' } },
  { headerName: 'Sales Unit',    field: 'unit_name',      width: 140, sortable: true, filter: 'agTextColumnFilter', cellStyle: { fontSize: '12px' } },
  { headerName: 'Region',        field: 'region',         width: 140, sortable: true, filter: 'agTextColumnFilter', cellStyle: { fontSize: '12px', color: '#6B7280' } },
  { headerName: 'Sales Incharge', field: 'sales_incharge', width: 130, sortable: true, filter: 'agTextColumnFilter', cellStyle: { fontSize: '12px' } },
  { headerName: 'Province', field: 'pj_province', width: 120, sortable: true, filter: 'agTextColumnFilter', cellStyle: { fontSize: '12px', color: '#6B7280' } },
  { headerName: 'Country',  field: 'pj_country',  width: 120, sortable: true, filter: 'agTextColumnFilter', cellStyle: { fontSize: '12px' },
    valueFormatter: p => countryName(p.value) }
];

const ALL_COLS = [
  { headerName: 'J Number', field: 'j_number', width: 115, pinned: 'left', sortable: true, filter: 'agTextColumnFilter',
    cellRenderer: p => <JLink data={p.data} value={p.value} pinned={p.node?.rowPinned} /> },
  { headerName: 'Type', field: 'record_type', width: 90, sortable: true,
    cellRenderer: p => p.value === 'contract'
      ? <span className="tw-px-1.5 tw-py-0.5 tw-rounded-sm tw-text-xs tw-font-bold tw-bg-blue-100 tw-text-blue-700">Contract</span>
      : <span className="tw-px-1.5 tw-py-0.5 tw-rounded-sm tw-text-xs tw-font-bold tw-bg-amber-100 tw-text-amber-700">Addendum</span> },
  { headerName: 'Number', field: 'display_number', width: 130, sortable: true, filter: 'agTextColumnFilter',
    valueFormatter: p => p.value || '—' },
  { headerName: 'Project',  field: 'project_name',  flex: 2, minWidth: 140, sortable: true, filter: 'agTextColumnFilter' },
  { headerName: 'Customer', field: 'customer_name', flex: 1, minWidth: 120, sortable: true, filter: 'agTextColumnFilter' },
  { headerName: 'Date', field: 'display_date', width: 120, sortable: true, filter: 'agDateColumnFilter',
    valueFormatter: p => fmtDate(p.value),
    cellStyle: p => ({ fontSize: '12px', color: p.data?.record_type === 'addendum' ? '#B45309' : '#6B7280' }) },
  { headerName: 'Value', field: 'display_value', width: 120, type: 'numericColumn', sortable: true, filter: 'agNumberColumnFilter',
    valueFormatter: p => fmtM(p.value),
    cellStyle: p => ({ textAlign: 'right', fontWeight: 600, color: p.data?.record_type === 'addendum' ? '#D97706' : '#1D4ED8' }) },
  { headerName: 'Weight (MT)', field: 'display_weight', width: 110, type: 'numericColumn', sortable: true, filter: 'agNumberColumnFilter',
    valueFormatter: p => Number(p.value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }), cellStyle: { textAlign: 'right' } },
  { headerName: 'Released By',  field: 'released_by',  width: 120, sortable: true, filter: 'agTextColumnFilter',
    cellStyle: { fontSize: '12px', color: '#374151' } },
  { headerName: 'Released Date', field: 'released_date', width: 120, sortable: true, filter: 'agDateColumnFilter',
    valueFormatter: p => fmtDate(p.value), cellStyle: { fontSize: '12px', color: '#059669' } },
  { headerName: 'Building', field: 'building_name',  width: 130, sortable: true, filter: 'agTextColumnFilter', cellStyle: { fontSize: '12px', color: '#374151' } },
  { headerName: 'Unit',     field: 'unit_name',      width: 100, sortable: true, filter: 'agTextColumnFilter', cellStyle: { fontSize: '12px' } },
  { headerName: 'Region',   field: 'region',         width: 90,  sortable: true, filter: 'agTextColumnFilter', cellStyle: { fontSize: '12px', color: '#6B7280' } },
  { headerName: 'Incharge', field: 'sales_incharge', width: 120, sortable: true, filter: 'agTextColumnFilter', cellStyle: { fontSize: '12px' } },
];

const ContractDetailedTable = ({
  data, pagination, totals, loading, error, baseParams, onPageChange, onPageSizeChange
}) => {
  const gridRef = useRef(null);
  const [tab,           setTab]           = useState('contracts');
  const [exporting,     setExporting]     = useState(false);
  const [admData,       setAdmData]       = useState([]);
  const [admPagination, setAdmPagination] = useState(null);
  const [admTotals,     setAdmTotals]     = useState(null);
  const [admLoading,    setAdmLoading]    = useState(false);
  const [admPage,       setAdmPage]       = useState(1);
  const [admPerPage,    setAdmPerPage]    = useState(50);
  const [allData,       setAllData]       = useState([]);
  const [allPagination, setAllPagination] = useState(null);
  const [allTotals,     setAllTotals]     = useState(null);
  const [allLoading,    setAllLoading]    = useState(false);
  const [allPage,       setAllPage]       = useState(1);
  const [allPerPage,    setAllPerPage]    = useState(50);
  const [allSortBy,     setAllSortBy]     = useState('display_date');
  const [allSortDir,    setAllSortDir]    = useState('DESC');

  useEffect(() => {
    if (tab !== 'addendums' || !baseParams) return;
    let cancelled = false;
    setAdmLoading(true);
    apiAxios.get('/crm/dashboard/contracts/get_addendum_data', {
      params: { ...baseParams, page: admPage, per_page: admPerPage }
    }).then(res => {
      if (cancelled) return;
      setAdmData(res.data?.data || []);
      setAdmPagination(res.data?.pagination || null);
      setAdmTotals(res.data?.totals || null);
    }).catch(() => {
      if (!cancelled) { setAdmData([]); setAdmPagination(null); setAdmTotals(null); }
    }).finally(() => { if (!cancelled) setAdmLoading(false); });
    return () => { cancelled = true; };
  }, [tab, baseParams, admPage, admPerPage]);

  useEffect(() => {
    if (tab !== 'all' || !baseParams) return;
    let cancelled = false;
    setAllLoading(true);
    apiAxios.get('/crm/dashboard/contracts/get_all_data', {
      params: { ...baseParams, page: allPage, per_page: allPerPage, sort_by: allSortBy, sort_dir: allSortDir }
    }).then(res => {
      if (cancelled) return;
      setAllData(res.data?.data || []);
      setAllPagination(res.data?.pagination || null);
      setAllTotals(res.data?.totals || null);
    }).catch(() => {
      if (!cancelled) { setAllData([]); setAllPagination(null); setAllTotals(null); }
    }).finally(() => { if (!cancelled) setAllLoading(false); });
    return () => { cancelled = true; };
  }, [tab, baseParams, allPage, allPerPage, allSortBy, allSortDir]);

  useEffect(() => { setAdmPage(1); setAllPage(1); }, [baseParams]);

  const defaultColDef = useMemo(() => ({
    resizable: true, suppressMenu: false, filterParams: { buttons: ['apply', 'clear'] }
  }), []);

  const handleExportContracts = useCallback(async () => {
    setExporting(true);
    try {
      const res = await apiAxios.get('/crm/dashboard/contracts/get_detailed_data',
        { params: { ...baseParams, per_page: 5000, page: 1 } });
      const rows = (res.data?.data || []).map(r => ({
        'J Number': r.j_number || r.q_number || '',
        'Contract #': r.contract_number || '',
        'Project': r.project_name || '',
        'Customer': r.customer_name || '',
        'Date': fmtDate(r.contract_date),
        'Contract Value': r.contract_value || 0,
        'Addendum Value': r.addendum_value || 0,
        'Total Value': r.grand_total_value || 0,
        'Weight (MT)': r.contract_weight || 0,
        'Addendums': r.addendum_count || 0,
        'Building': r.building_name || '',
        'Released By': r.released_by || '',
        'Released Date': fmtDate(r.released_date),
        'Sales Unit': r.unit_name || '',
        'Region': r.region || '',
        'Type': r.type_of_sales || '',
        'Sales Incharge': r.sales_incharge || '',
        'Province': r.pj_province || '',
        'Country': countryName(r.pj_country),
      }));
      ExportToExcel(rows, `Contracts_${new Date().toISOString().slice(0, 10)}`);
    } catch (e) { /* silently fail */ } finally { setExporting(false); }
  }, [baseParams]);

  const handleExportAddendums = useCallback(async () => {
    setExporting(true);
    try {
      const res = await apiAxios.get('/crm/dashboard/contracts/get_addendum_data',
        { params: { ...baseParams, per_page: 5000, page: 1 } });
      const rows = (res.data?.data || []).map(r => ({
        'J Number': r.j_number || r.q_number || '',
        'Contract #': r.contract_number || '',
        'Addendum #': r.addendum_number || '',
        'Project': r.project_name || '',
        'Customer': r.customer_name || '',
        'Addendum Date': fmtDate(r.addendum_date),
        'Contract Date': fmtDate(r.contract_date),
        'Addendum Value': r.addendum_value || 0,
        'Addendum Weight (MT)': r.addendum_weight || 0,
        'Parent Contract Value': r.parent_contract_value || 0,
        'Signed By': r.signed_by || '',
        'Building': r.building_name || '',
        'Released By': r.released_by || '',
        'Released Date': fmtDate(r.released_date),
        'Sales Unit': r.unit_name || '',
        'Region': r.region || '',
        'Sales Incharge': r.sales_incharge || '',
        'Province': r.pj_province || '',
        'Country': countryName(r.pj_country),
      }));
      ExportToExcel(rows, `Addendums_${new Date().toISOString().slice(0, 10)}`);
    } catch (e) { /* silently fail */ } finally { setExporting(false); }
  }, [baseParams]);

  const handleExportAll = useCallback(async () => {
    setExporting(true);
    try {
      const res = await apiAxios.get('/crm/dashboard/contracts/get_all_data', {
        params: { ...baseParams, page: 1, per_page: 5000, sort_by: allSortBy, sort_dir: allSortDir }
      });
      const rows = (res.data?.data || []).map(r => ({
        'Type': r.record_type === 'contract' ? 'Contract' : 'Addendum',
        'J Number': r.j_number || '',
        'Number': r.display_number || '',
        'Project': r.project_name || '',
        'Customer': r.customer_name || '',
        'Date': fmtDate(r.display_date),
        'Value': r.display_value || 0,
        'Weight (MT)': r.display_weight || 0,
        'Building': r.building_name || '',
        'Released By': r.released_by || '',
        'Released Date': fmtDate(r.released_date),
        'Unit': r.unit_name || '',
        'Region': r.region || '',
        'Incharge': r.sales_incharge || '',
      }));
      ExportToExcel(rows, `All_Records_${new Date().toISOString().slice(0, 10)}`);
    } catch (e) { /* silently fail */ } finally { setExporting(false); }
  }, [baseParams, allSortBy, allSortDir]);

  const isContracts = tab === 'contracts';
  const isAddendums = tab === 'addendums';
  const isAll       = tab === 'all';

  const activeCols    = isContracts ? CONTRACT_COLS : isAddendums ? ADDENDUM_COLS : ALL_COLS;
  const activeData    = isContracts ? (data || []) : isAddendums ? admData : allData;
  const activePag     = isContracts ? pagination : isAddendums ? admPagination : allPagination;
  const activeLoading = isContracts ? loading : isAddendums ? admLoading : allLoading;
  const activeRecords = activePag?.total_records || 0;
  const handleExport  = isContracts ? handleExportContracts : isAddendums ? handleExportAddendums : handleExportAll;

  const handleAllSortChanged = useCallback((e) => {
    const sortState = e.columnApi?.getColumnState?.()?.find(c => c.sort) ||
                      e.api?.getColumnState?.()?.find(c => c.sort);
    if (!sortState) return;
    const fieldMap = {
      display_date: 'display_date', display_value: 'display_value',
      display_weight: 'display_weight', j_number: 'j_number',
      project_name: 'project_name', customer_name: 'customer_name',
      released_by: 'released_by', released_date: 'released_date',
      building_name: 'building_name',
    };
    const newSortBy = fieldMap[sortState.colId] || 'display_date';
    const newSortDir = (sortState.sort || 'desc').toUpperCase();
    setAllSortBy(newSortBy);
    setAllSortDir(newSortDir);
    setAllPage(1);
  }, []);

  const pinnedBottomRowData = useMemo(() => {
    if (tab === 'contracts') {
      if (!totals) return [];
      return [{ j_number: '__footer__', ...totals }];
    }
    if (tab === 'addendums') {
      if (!admTotals) return [];
      return [{ j_number: '__footer__', ...admTotals }];
    }
    if (!allTotals) return [];
    return [{ j_number: '__footer__', ...allTotals }];
  }, [tab, totals, admTotals, allTotals]);

  const getRowStyle = useCallback((params) => {
    if (params.node.rowPinned === 'bottom') {
      return { background: '#F1F5F9', fontWeight: 700, borderTop: '2px solid #CBD5E1', fontSize: '12px' };
    }
    return undefined;
  }, []);

  return (
    <Card className="tw-border tw-border-gray-200 tw-shadow-sm">
      <div className="tw-px-5 tw-pt-4 tw-pb-0 tw-border-b tw-border-gray-200">
        <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap tw-mb-3">
          <FiList className="tw-text-gray-500" size={18} />
          <h3 className="tw-font-bold tw-text-gray-900">Detailed Records</h3>
          <span className="tw-text-xs tw-text-gray-400">{activeRecords.toLocaleString()} records</span>
          <div className="tw-ml-auto">
            <Button
              variant="secondary"
              size="sm"
              leftIcon={FiDownload}
              disabled={exporting || activeRecords === 0}
              onClick={handleExport}
            >
              {exporting ? 'Exporting…' : 'Export Excel'}
            </Button>
          </div>
        </div>

        <div className="tw-flex tw-items-center tw-gap-1">
          <button onClick={() => setTab('contracts')}
            className={`tw-flex tw-items-center tw-gap-1.5 tw-px-4 tw-py-2 tw-text-xs tw-font-semibold tw-border-0 tw-cursor-pointer tw-transition-colors tw-rounded-t ${isContracts ? 'tw-bg-blue-600 tw-text-white' : 'tw-bg-gray-100 tw-text-gray-600 hover:tw-bg-gray-200'}`}>
            <FiFileText size={12} />
            Contracts
            {pagination?.total_records > 0 && (
              <span className={`tw-ml-1 tw-px-1.5 tw-py-0.5 tw-rounded-xl tw-text-xs ${isContracts ? 'tw-bg-white/25 tw-text-white' : 'tw-bg-gray-300 tw-text-gray-700'}`}>
                {(pagination?.total_records || 0).toLocaleString()}
              </span>
            )}
          </button>
          <button onClick={() => setTab('addendums')}
            className={`tw-flex tw-items-center tw-gap-1.5 tw-px-4 tw-py-2 tw-text-xs tw-font-semibold tw-border-0 tw-cursor-pointer tw-transition-colors tw-rounded-t ${isAddendums ? 'tw-bg-amber-500 tw-text-white' : 'tw-bg-gray-100 tw-text-gray-600 hover:tw-bg-gray-200'}`}>
            <FiPlusSquare size={12} />
            Addendums
            {admPagination?.total_records > 0 && (
              <span className={`tw-ml-1 tw-px-1.5 tw-py-0.5 tw-rounded-xl tw-text-xs ${isAddendums ? 'tw-bg-white/25 tw-text-white' : 'tw-bg-gray-300 tw-text-gray-700'}`}>
                {(admPagination?.total_records || 0).toLocaleString()}
              </span>
            )}
          </button>
          <button onClick={() => setTab('all')}
            className={`tw-flex tw-items-center tw-gap-1.5 tw-px-4 tw-py-2 tw-text-xs tw-font-semibold tw-border-0 tw-cursor-pointer tw-transition-colors tw-rounded-t ${isAll ? 'tw-bg-violet-600 tw-text-white' : 'tw-bg-gray-100 tw-text-gray-600 hover:tw-bg-gray-200'}`}>
            <FiLayers size={12} />
            All
            {allPagination?.total_records > 0 && (
              <span className={`tw-ml-1 tw-px-1.5 tw-py-0.5 tw-rounded-xl tw-text-xs ${isAll ? 'tw-bg-white/25 tw-text-white' : 'tw-bg-gray-300 tw-text-gray-700'}`}>
                {(allPagination?.total_records || 0).toLocaleString()}
              </span>
            )}
          </button>
        </div>
      </div>


      {error && isContracts && (
        <div className="tw-px-5 tw-py-3 tw-bg-red-50 tw-border-b tw-border-red-100">
          <p className="tw-text-red-600 tw-text-sm">Error loading records</p>
        </div>
      )}

      <div className="tw-relative">
        {activeLoading && <LoadingSpinner overlay message="Loading records…" />}
        <div className="ag-theme-quartz"
          style={{ height: 420, '--ag-font-size': '12px', '--ag-row-height': '36px', '--ag-header-height': '36px' }}>
          <AgGridReact
            ref={gridRef}
            rowData={activeData}
            columnDefs={activeCols}
            defaultColDef={defaultColDef}
            suppressRowClickSelection
            animateRows
            onSortChanged={isAll ? handleAllSortChanged : undefined}
            overlayNoRowsTemplate='<span style="color:#94a3b8;font-size:12px">No records found</span>'
            pinnedBottomRowData={pinnedBottomRowData}
            getRowStyle={getRowStyle}
          />
        </div>
      </div>

      {activePag && (
        <div className="tw-px-5 tw-py-2.5 tw-border-t tw-border-gray-100 tw-bg-gray-50">
          <Pagination
            currentPage={activePag.current_page || 1}
            totalPages={activePag.total_pages || 1}
            totalResults={activePag.total_records || 0}
            perPage={activePag.per_page || 50}
            onPageChange={
              isContracts ? onPageChange
              : isAddendums ? (p) => setAdmPage(p)
              : (p) => setAllPage(p)
            }
            onPageSizeChange={
              isContracts ? onPageSizeChange
              : isAddendums ? (s) => { setAdmPerPage(s); setAdmPage(1); }
              : (s) => { setAllPerPage(s); setAllPage(1); }
            }
            pageSizeOptions={[20, 50, 100, 200]}
            disabled={activeLoading}
          />
        </div>
      )}
    </Card>
  );
};

ContractDetailedTable.propTypes = {
  data:             PropTypes.array,
  pagination:       PropTypes.object,
  totals:           PropTypes.object,
  loading:          PropTypes.bool,
  error:            PropTypes.any,
  baseParams:       PropTypes.object,
  onPageChange:     PropTypes.func.isRequired,
  onPageSizeChange: PropTypes.func.isRequired
};
ContractDetailedTable.defaultProps = {
  data: [], pagination: null, totals: null, loading: false, error: null, baseParams: {}
};

export default ContractDetailedTable;
